import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PHOTOGRAPHERS_PAGE } from '@/constants/testIds';

export const PRICE_MAX = 5000; // dollars — slider ceiling ("$5,000+")
export const DISTANCE_MAX = 100; // miles

const sectionTitle = 'text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-muted))]';

export const StyleChips = ({ styles, selected, onToggle, testId, chipTestId, size = 'md' }) => (
  <div data-testid={testId} className="flex flex-wrap gap-2">
    {styles.map((s) => {
      const active = selected.includes(s);
      return (
        <button
          key={s}
          type="button"
          onClick={() => onToggle(s)}
          aria-pressed={active}
          data-testid={chipTestId(s)}
          className={`rounded-full border uppercase tracking-[0.1em] transition-[background-color,border-color,color,transform] duration-150 active:scale-[0.98] ${
            size === 'md' ? 'px-3 py-1.5 text-[11px]' : 'px-2.5 py-1 text-[10px]'
          } ${
            active
              ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))]/15 text-[hsl(var(--lxra-gold-hover))]'
              : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))]/50'
          }`}
        >
          {s}
        </button>
      );
    })}
  </div>
);

/** Sticky filter rail (desktop) / drawer body (mobile). Pure controlled component. */
export const DirectoryFilters = ({ styles, filters, onChange, hasOrigin, onClear, activeCount }) => {
  const toggleStyle = (s) => onChange({ styles: filters.styles.includes(s) ? filters.styles.filter((x) => x !== s) : [...filters.styles, s] });
  return (
    <div data-testid={PHOTOGRAPHERS_PAGE.filtersPanel} className="space-y-7">
      <div className="flex items-center justify-between">
        <p className="font-display text-lg tracking-[0.08em] text-[hsl(var(--lxra-fg))]">FILTERS</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={activeCount === 0}
          data-testid={PHOTOGRAPHERS_PAGE.filtersClear}
          className="h-8 text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] disabled:opacity-40"
        >
          Clear{activeCount ? ` (${activeCount})` : ''}
        </Button>
      </div>

      <div className="space-y-3">
        <p className={sectionTitle}>Style</p>
        <StyleChips styles={styles} selected={filters.styles} onToggle={toggleStyle} testId={PHOTOGRAPHERS_PAGE.styleChips} chipTestId={PHOTOGRAPHERS_PAGE.styleChip} size="sm" />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className={sectionTitle}>Starting price</p>
          <p data-testid={PHOTOGRAPHERS_PAGE.priceReadout} className="text-xs lxra-mono text-[hsl(var(--lxra-fg))]">
            ${filters.price[0].toLocaleString()} – {filters.price[1] >= PRICE_MAX ? `$${PRICE_MAX.toLocaleString()}+` : `$${filters.price[1].toLocaleString()}`}
          </p>
        </div>
        <Slider
          data-testid={PHOTOGRAPHERS_PAGE.priceSlider}
          min={0}
          max={PRICE_MAX}
          step={50}
          value={filters.price}
          onValueChange={(v) => onChange({ price: v })}
          className="[&_[role=slider]]:bg-[hsl(var(--lxra-gold))] [&_[role=slider]]:border-[hsl(var(--lxra-gold))] [&_.bg-primary]:bg-[hsl(var(--lxra-gold))]"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className={sectionTitle}>Distance</p>
          <p data-testid={PHOTOGRAPHERS_PAGE.distanceReadout} className="text-xs lxra-mono text-[hsl(var(--lxra-fg))]">
            {filters.distance >= DISTANCE_MAX ? 'Any distance' : `Within ${filters.distance} mi`}
          </p>
        </div>
        <Slider
          data-testid={PHOTOGRAPHERS_PAGE.distanceSlider}
          min={5}
          max={DISTANCE_MAX}
          step={5}
          value={[filters.distance]}
          onValueChange={(v) => onChange({ distance: v[0] })}
          disabled={!hasOrigin}
          className="[&_[role=slider]]:bg-[hsl(var(--lxra-gold))] [&_[role=slider]]:border-[hsl(var(--lxra-gold))] [&_.bg-primary]:bg-[hsl(var(--lxra-gold))] disabled:opacity-50"
        />
        {!hasOrigin && <p className="text-[11px] text-[hsl(var(--lxra-muted))]">Enter a location to filter by distance.</p>}
      </div>

      <div className="flex items-center justify-between rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-3">
        <div>
          <Label className="text-sm text-[hsl(var(--lxra-fg))]">Verified LXRA only</Label>
          <p className="text-[11px] text-[hsl(var(--lxra-muted))]">Studios reviewed to LXRA standards</p>
        </div>
        <Switch
          checked={filters.verifiedOnly}
          onCheckedChange={(v) => onChange({ verifiedOnly: v })}
          data-testid={PHOTOGRAPHERS_PAGE.verifiedSwitch}
          className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]"
        />
      </div>

      <div className="space-y-2">
        <p className={sectionTitle}>Available on</p>
        <Input
          type="date"
          value={filters.availableOn}
          onChange={(e) => onChange({ availableOn: e.target.value })}
          data-testid={PHOTOGRAPHERS_PAGE.availableOnInput}
          className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]"
        />
        <p className="text-[11px] text-[hsl(var(--lxra-muted))]">Only show photographers open on that date.</p>
      </div>
    </div>
  );
};
